import { useMe } from '../hooks/useMe'
import { useDashboard, useWeather } from '../hooks/useDashboard'
import { DashboardHeader } from '../components/dashboard/DashboardHeader'
import { KpiRow } from '../components/dashboard/KpiRow'
import { LiveBanner } from '../components/dashboard/LiveBanner'
import { UpcomingEvents } from '../components/dashboard/UpcomingEvents'
import { LiveFeed } from '../components/dashboard/LiveFeed'
import { MiniCalendar } from '../components/dashboard/MiniCalendar'
import { WeatherStrip } from '../components/dashboard/WeatherStrip'
import { QuickActions } from '../components/dashboard/QuickActions'
import { AdminDiagnostics } from '../components/dashboard/AdminDiagnostics'

// Dashboard - the Heritage Operations Centre home. Layout matches the v5
// prototype: KPI row across the top, optional Live banner when an event is
// running today, a two-column body underneath (left ~⅔ = upcoming + live
// feed; right ~⅓ = mini-calendar + weather + quick actions).
//
// Two parallel queries:
//   - useDashboard() polls every 60s for KPIs / banner / upcoming / feed.
//   - useWeather() polls every 60min and shares cache across pages.
// Weather failure is treated softly (component shows "unavailable" rather
// than blocking the rest of the page).
//
// The original foundation-slice diagnostics still live here, moved into
// `AdminDiagnostics` and collapsed by default. Useful for production
// debugging without needing a separate /diag route.

export function Dashboard() {
  const { data: me } = useMe()
  const { data, isLoading, error } = useDashboard()
  const weather = useWeather()

  return (
    <div className="space-y-5">
      <DashboardHeader me={me ?? null} />

      <KpiRow kpis={data?.kpis} loading={isLoading} />

      {error && (
        <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-3 text-sm">
          Could not load dashboard data. The KPIs above show placeholders.
        </div>
      )}

      {data?.liveBanner && <LiveBanner banner={data.liveBanner} />}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <UpcomingEvents events={data?.upcoming ?? []} loading={isLoading} />
          <LiveFeed
            feed={data?.feed ?? []}
            loading={isLoading}
            showSeeAll={!!me?.isAdmin}
          />
        </div>
        <aside className="space-y-5">
          <MiniCalendar />
          <WeatherStrip
            data={weather.data}
            loading={weather.isLoading}
            errored={!!weather.error && !weather.data}
          />
          <QuickActions />
        </aside>
      </div>

      <AdminDiagnostics />
    </div>
  )
}
